import { useState, type ChangeEvent } from 'react';
import { Button, Checkbox, Input, Separator } from '@predoc/cow';
import { Hospital } from 'lucide-react';
import { useDebounce } from '@/hooks';
import { useListProfiles } from '@/feature/profiles/hooks/profilesAPI';
import { getProfileColor } from '@/utils';
import { cn } from '@/lib/utils';
import type { ProfileFilterState } from '../../types';
import {
  getIsProfileSelected,
  getNextProfileFilter,
  selectAllProfiles,
  selectNoProfiles,
} from './profileFilter';

const PROFILE_PAGE_LIMIT = 100;

interface ProfileFilterListProps {
  profileFilter: ProfileFilterState;
  onProfileFilterChange: (profileFilter: ProfileFilterState) => void;
  /** Total assigned profiles, used for the "showing first N" hint. */
  totalProfileCount: number;
  listClassName?: string;
  autoFocusSearch?: boolean;
}

/**
 * Searchable profile checkbox list with Select All/None actions. Shared by the
 * patients list filter and the efax search bar; each owns its own trigger.
 */
export const ProfileFilterList = ({
  profileFilter,
  onProfileFilterChange,
  totalProfileCount,
  listClassName,
  autoFocusSearch = false,
}: ProfileFilterListProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);

  const { data: profilesData, isLoading } = useListProfiles({
    assignedProfilesOnly: true,
    searchTerms: debouncedSearch ? [debouncedSearch] : undefined,
    pagination: { offset: 0, limit: PROFILE_PAGE_LIMIT },
  });
  const profiles = profilesData?.data ?? [];

  const handleProfileChange = (profileId: string, checked: boolean) => {
    onProfileFilterChange(
      getNextProfileFilter(profileFilter, profileId, checked),
    );
  };

  return (
    <>
      <div className="p-2 pb-0">
        <Input
          placeholder="Search profiles..."
          value={searchTerm}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(event.target.value)
          }
          className="h-8 text-sm"
          autoFocus={autoFocusSearch}
        />
      </div>
      {!debouncedSearch && totalProfileCount > PROFILE_PAGE_LIMIT && (
        <p className="text-muted-foreground px-2 pt-1 text-xs">
          Showing first {PROFILE_PAGE_LIMIT} of {totalProfileCount}. Search to
          find more.
        </p>
      )}
      <div className={cn('overflow-y-auto', listClassName)}>
        {isLoading ? (
          <div className="flex items-center justify-center p-4">
            <span className="text-muted-foreground text-sm">Loading...</span>
          </div>
        ) : profiles.length === 0 ? (
          <div className="flex items-center justify-center p-4">
            <span className="text-muted-foreground text-sm">
              No profiles found.
            </span>
          </div>
        ) : (
          <div className="p-2">
            {profiles.map((profile) => {
              const isSelected = getIsProfileSelected(
                profileFilter,
                profile.id,
              );
              const profileColor = getProfileColor(profile.avatarType);

              return (
                <div key={profile.id} className="flex items-center py-1">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked: boolean) =>
                      handleProfileChange(profile.id, checked)
                    }
                    label={
                      <div className="inline-flex items-center gap-2 whitespace-nowrap">
                        <Hospital
                          className="size-4"
                          style={{ color: profileColor }}
                          aria-hidden="true"
                        />
                        <span className="text-sm">{profile.displayName}</span>
                      </div>
                    }
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
      {profiles.length > 0 && (
        <>
          <Separator />
          <div className="flex flex-row justify-start gap-2 p-2">
            <Button
              type="button"
              variant="link"
              className="h-auto p-0"
              onClick={() => onProfileFilterChange(selectAllProfiles())}
              disabled={isLoading}
            >
              Select All
            </Button>
            <Button
              type="button"
              variant="link"
              className="h-auto p-0"
              onClick={() => onProfileFilterChange(selectNoProfiles())}
              disabled={isLoading}
            >
              Select None
            </Button>
          </div>
        </>
      )}
    </>
  );
};
